import React, { useState, useEffect } from "react";

const API_BASE_URL = "https://api.yogavantha.com";


const MemberRegistration = () => {
  const [formData, setFormData] = useState({
    admissionNo: "",
    branch: "",
    batch: "",
    name: "",
    photo: null,
    photoPreview: "https://placehold.co/100x100/e0e0e0/000000?text=No+Photo",
    dateOfBirth: "",
    age: "",
    phoneNo: "",
    email: "",
    occupation: "",
    address: "",
    anythingToKnow: "",
    howYouKnow: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    registrationDate: new Date().toISOString().split("T")[0],
    waiverAccepted: false,
  });

  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  // Fetch branches on component mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/branches/`);
        if (!res.ok) throw new Error("Failed to fetch branches");
        const data = await res.json();
        setBranches(data);
      } catch (error) {
        console.error("Branches fetch error:", error);
      }
    };
    fetchBranches();
  }, []);

  // Fetch batches when branch changes
  useEffect(() => {
    if (!formData.branch) {
      setBatches([]);
      setFormData((prev) => ({ ...prev, batch: "" }));
      return;
    }
    const fetchBatches = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/batches/?branch_id=${formData.branch}`);
        if (!res.ok) throw new Error("Failed to fetch batches");
        const data = await res.json();
        setBatches(data);
      } catch (error) {
        console.error("Batches fetch error:", error);
      }
    };
    fetchBatches();
  }, [formData.branch]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Calculate age from DOB
  useEffect(() => {
    if (!formData.dateOfBirth) {
      setFormData((prev) => ({ ...prev, age: "" }));
      return;
    }
    const dob = new Date(formData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    setFormData((prev) => ({ ...prev, age: age.toString() }));
  }, [formData.dateOfBirth]);

  // Handle photo upload and preview
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({ ...prev, photoPreview: event.target.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({
        ...prev,
        photo: null,
        photoPreview: "https://placehold.co/100x100/e0e0e0/000000?text=No+Photo",
      }));
    }
  };

  // Client-side validation
  const validate = () => {
    const newErrors = {};
    if (!formData.admissionNo.trim()) newErrors.admissionNo = "Admission Number is required.";
    if (!formData.branch) newErrors.branch = "Branch is required.";
    if (!formData.batch) newErrors.batch = "Batch is required.";
    if (!formData.name.trim()) newErrors.name = "Full name is required.";
    if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required.";
    if (!formData.phoneNo.match(/^\d{10}$/)) newErrors.phoneNo = "Phone number must be 10 digits.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email address.";
    if (!formData.occupation.trim()) newErrors.occupation = "Occupation is required.";
    if (!formData.address.trim()) newErrors.address = "Address is required.";
    if (!formData.emergencyContactName.trim()) newErrors.emergencyContactName = "Emergency Contact Name is required.";
    if (!formData.emergencyContactPhone.match(/^\d{10}$/))
      newErrors.emergencyContactPhone = "Emergency Contact Phone must be 10 digits.";
    if (!formData.waiverAccepted) newErrors.waiverAccepted = "You must accept the waiver.";
    return newErrors;
  };

  // Submit form data
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setSubmitMessage(null);

    try {
      const payload = new FormData();
      payload.append("admissionNo", formData.admissionNo);
      payload.append("branchCode", formData.branch); // Sending branch code if your backend expects
      payload.append("batchId", formData.batch);
      payload.append("name", formData.name);
      if (formData.photo) payload.append("photo", formData.photo);
      payload.append("dateOfBirth", formData.dateOfBirth);
      payload.append("age", formData.age);
      payload.append("mobileNumber", formData.phoneNo);
      if (formData.email) payload.append("email", formData.email);
      payload.append("occupation", formData.occupation);
      payload.append("address", formData.address);
      if (formData.anythingToKnow) payload.append("notes", formData.anythingToKnow); // Map to your schema fields
      if (formData.howYouKnow) payload.append("howYouKnow", formData.howYouKnow);
      payload.append("emergencyContactName", formData.emergencyContactName);
      payload.append("emergencyContactPhone", formData.emergencyContactPhone);
      payload.append("dateOfJoining", formData.registrationDate);
      payload.append("waiverAccepted", formData.waiverAccepted ? "true" : "false");

      const res = await fetch(`${API_BASE_URL}/members/`, {
        method: "POST",
        body: payload,
        // Do not set Content-Type header for FormData
      });

      if (res.ok) {
        setSubmitMessage({ success: true, text: "Member registered successfully!" });
        // Reset form
        setFormData({
          admissionNo: "",
          branch: "",
          batch: "",
          name: "",
          photo: null,
          photoPreview: "https://placehold.co/100x100/e0e0e0/000000?text=No+Photo",
          dateOfBirth: "",
          age: "",
          phoneNo: "",
          email: "",
          occupation: "",
          address: "",
          anythingToKnow: "",
          howYouKnow: "",
          emergencyContactName: "",
          emergencyContactPhone: "",
          registrationDate: new Date().toISOString().split("T")[0],
          waiverAccepted: false,
        });
        setBatches([]);
        setErrors({});
      } else {
        const errData = await res.json();
        setSubmitMessage({ success: false, text: errData.detail || "Failed to register member." });
      }
    } catch (error) {
      setSubmitMessage({ success: false, text: `Network error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-md shadow-md my-8">
      <h1 className="text-3xl font-bold mb-6 text-center">Member Registration</h1>

      {submitMessage && (
        <div
          className={`mb-6 p-4 rounded ${submitMessage.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            }`}
        >
          {submitMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">

          {/* Admission No */}
          <div>
            <label htmlFor="admissionNo" className="font-semibold mb-1 block">Admission No. *</label>
            <input
              type="text"
              id="admissionNo"
              name="admissionNo"
              value={formData.admissionNo}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.admissionNo ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.admissionNo && <p className="text-red-600 text-sm mt-1">{errors.admissionNo}</p>}
          </div>

          {/* Branch */}
          <div>
            <label htmlFor="branch" className="font-semibold mb-1 block">Branch *</label>
            <select
              id="branch"
              name="branch"
              value={formData.branch}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.branch ? "border-red-600" : "border-gray-300"
                }`}
              required
            >
              <option value="">-- Select Branch --</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
            {errors.branch && <p className="text-red-600 text-sm mt-1">{errors.branch}</p>}
          </div>

          {/* Batch */}
          <div>
            <label htmlFor="batch" className="font-semibold mb-1 block">Batch *</label>
            <select
              id="batch"
              name="batch"
              value={formData.batch}
              disabled={!formData.branch}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.batch ? "border-red-600" : "border-gray-300"
                }`}
              required
            >
              <option value="">-- Select Batch --</option>
              {batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
            {errors.batch && <p className="text-red-600 text-sm mt-1">{errors.batch}</p>}
          </div>

          {/* Full Name */}
          <div>
            <label htmlFor="name" className="font-semibold mb-1 block">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.name ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="dateOfBirth" className="font-semibold mb-1 block">Date of Birth *</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              max={new Date().toISOString().split("T")[0]}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.dateOfBirth ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.dateOfBirth && <p className="text-red-600 text-sm mt-1">{errors.dateOfBirth}</p>}
          </div>

          {/* Age (read-only) */}
          <div>
            <label htmlFor="age" className="font-semibold mb-1 block">Age</label>
            <input
              type="text"
              id="age"
              name="age"
              value={formData.age}
              readOnly
              className="w-full border border-gray-300 rounded bg-gray-100 cursor-not-allowed px-3 py-2"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="phoneNo" className="font-semibold mb-1 block">Phone Number *</label>
            <input
              type="tel"
              id="phoneNo"
              name="phoneNo"
              maxLength={10}
              value={formData.phoneNo}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.phoneNo ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.phoneNo && <p className="text-red-600 text-sm mt-1">{errors.phoneNo}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="font-semibold mb-1 block">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.email ? "border-red-600" : "border-gray-300"
                }`}
            />
            {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Occupation */}
          <div>
            <label htmlFor="occupation" className="font-semibold mb-1 block">Occupation *</label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.occupation ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.occupation && <p className="text-red-600 text-sm mt-1">{errors.occupation}</p>}
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label htmlFor="address" className="font-semibold mb-1 block">Address *</label>
            <textarea
              id="address"
              name="address"
              rows={3}
              value={formData.address}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.address ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.address && <p className="text-red-600 text-sm mt-1">{errors.address}</p>}
          </div>

          {/* Anything to know */}
          <div className="md:col-span-2">
            <label htmlFor="anythingToKnow" className="font-semibold mb-1 block">Is there anything you would like us to know?</label>
            <textarea
              id="anythingToKnow"
              name="anythingToKnow"
              rows={3}
              value={formData.anythingToKnow}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          {/* How did you know */}
          <div className="md:col-span-2">
            <label htmlFor="howYouKnow" className="font-semibold mb-1 block">How you got to know about us</label>
            <input
              type="text"
              id="howYouKnow"
              name="howYouKnow"
              value={formData.howYouKnow}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          {/* Emergency Contact Name */}
          <div>
            <label htmlFor="emergencyContactName" className="font-semibold mb-1 block">Emergency Contact Name *</label>
            <input
              type="text"
              id="emergencyContactName"
              name="emergencyContactName"
              value={formData.emergencyContactName}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.emergencyContactName ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.emergencyContactName && <p className="text-red-600 text-sm mt-1">{errors.emergencyContactName}</p>}
          </div>

          {/* Emergency Contact Phone */}
          <div>
            <label htmlFor="emergencyContactPhone" className="font-semibold mb-1 block">Emergency Contact Phone Number *</label>
            <input
              type="tel"
              id="emergencyContactPhone"
              name="emergencyContactPhone"
              maxLength={10}
              value={formData.emergencyContactPhone}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.emergencyContactPhone ? "border-red-600" : "border-gray-300"
                }`}
              required
            />
            {errors.emergencyContactPhone && <p className="text-red-600 text-sm mt-1">{errors.emergencyContactPhone}</p>}
          </div>

          {/* Registration Date */}
          <div className="md:col-span-2">
            <label htmlFor="registrationDate" className="font-semibold mb-1 block">Registration Date</label>
            <input
              type="date"
              id="registrationDate"
              name="registrationDate"
              value={formData.registrationDate}
              readOnly
              className="w-full border border-gray-300 rounded bg-gray-100 cursor-not-allowed px-3 py-2"
            />
          </div>

          {/* Photo Upload */}
          <div className="md:col-span-2 flex flex-col items-center mb-4">
            <img
              src={formData.photoPreview}
              alt="Member Preview"
              className="w-24 h-24 object-cover rounded-full border-2 border-purple-600 shadow mb-3"
            />
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="mb-4"
            />
          </div>

          {/* Waiver Acceptance */}
          <div className="md:col-span-2 flex items-center">
            <input
              type="checkbox"
              id="waiverAccepted"
              name="waiverAccepted"
              checked={formData.waiverAccepted}
              onChange={handleChange}
              className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              required
            />
            <label htmlFor="waiverAccepted" className="ml-2">I have read and agree to the terms of the waiver *</label>
          </div>
          {errors.waiverAccepted && <p className="text-red-600 text-sm md:col-span-2">{errors.waiverAccepted}</p>}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded font-semibold"
        >
          {loading ? "Registering..." : "Register Member"}
        </button>
      </form>
    </div>
  );
};

export default MemberRegistration;
